const MAGIC = 'H3D0'
const HEADER_SIZE = 20
const UINT32S_PER_PRIMITIVE = 3
const FLOATS_PER_VERTEX = 3
const FLOATS_PER_COLOR = 4
const BYTES_PER_FLOAT32 = 4
const BYTES_PER_UINT32 = 4

/**
 * Punctual lights live in an optional section appended after the material table.
 *
 * The 20-byte header is deliberately left alone: a model without lights writes no
 * section at all and stays byte-identical to what earlier builds produced, and a reader
 * that predates lights stops after the materials and ignores the trailing bytes. That
 * keeps H3D0 a prototype format rather than forcing a version bump.
 *
 * There are two record layouts, told apart by the section magic:
 *
 *   'LGT0'  uint32 lightCount
 *   per light: uint32 type, float32[3] world position, float32[3] linear RGB,
 *              float32 intensity, uint32 idByteLength, UTF-8 id padded to 4 bytes
 *
 *   'LGT1'  uint32 lightCount
 *   per light: uint32 recordByteLength, uint32 type, float32[3] world position,
 *              float32[3] world direction, float32[3] linear RGB, float32 intensity,
 *              float32 innerConeAngle, float32 outerConeAngle, uint32 idByteLength,
 *              UTF-8 id padded to 4 bytes
 *
 * LGT0 has no per-record length, so it can never carry a field an older reader could
 * skip - which is why Spot got a second layout instead of an extension. LGT1 records
 * carry their own length, so a future field can be appended and skipped safely.
 *
 * A Point-only model still writes LGT0, byte for byte what it wrote before Spot
 * existed; LGT1 appears only once a model actually contains a Spot.
 *
 * `intensity` is the glTF value as authored, and the cone angles are in radians as
 * authored. The 0..100 action scale is a runtime interpretation and is deliberately
 * not baked into the file.
 */
const LIGHT_SECTION_MAGIC_V0 = 'LGT0'
const LIGHT_SECTION_MAGIC_V1 = 'LGT1'
const LIGHT_SECTION_HEADER_SIZE = 8
const LIGHT_TYPE_POINT = 0
const LIGHT_TYPE_SPOT = 1
const LIGHT_TYPE_IDS = { point: LIGHT_TYPE_POINT, spot: LIGHT_TYPE_SPOT }
const LIGHT_TYPE_NAMES = { [LIGHT_TYPE_POINT]: 'point', [LIGHT_TYPE_SPOT]: 'spot' }
const FLOATS_PER_LIGHT_VECTOR = 3
// type + position + rgb + intensity + idByteLength
const LIGHT_V0_FIXED_SIZE = BYTES_PER_UINT32 * 2 + BYTES_PER_FLOAT32 * 7
// recordByteLength + type + position + direction + rgb + intensity + inner + outer + idByteLength
const LIGHT_V1_FIXED_SIZE = BYTES_PER_UINT32 * 3 + BYTES_PER_FLOAT32 * 12
// KHR_lights_punctual spot defaults and range.
const DEFAULT_INNER_CONE_ANGLE = 0
const DEFAULT_OUTER_CONE_ANGLE = Math.PI / 4
const MAX_CONE_ANGLE = Math.PI / 2

export function writeH3dPrototype({ positions, normals, indices, primitives, materials, baseColor, lights }) {
  validatePositions(positions)
  validateNormals(normals, positions.length)
  const vertexCount = positions.length / FLOATS_PER_VERTEX
  validateIndices(indices, vertexCount)
  const normalizedMaterials = normalizeMaterials(materials, baseColor)
  const normalizedPrimitives = normalizePrimitives(primitives, indices.length)
  const normalizedLights = normalizeLights(lights)
  validateMaterials(normalizedMaterials)
  validatePrimitives(normalizedPrimitives, indices.length, normalizedMaterials.length)

  const indexCount = indices.length
  const primitiveCount = normalizedPrimitives.length
  const materialCount = normalizedMaterials.length
  const byteLength = HEADER_SIZE
    + positions.length * BYTES_PER_FLOAT32
    + normals.length * BYTES_PER_FLOAT32
    + indices.length * BYTES_PER_UINT32
    + normalizedPrimitives.length * UINT32S_PER_PRIMITIVE * BYTES_PER_UINT32
    + normalizedMaterials.length * FLOATS_PER_COLOR * BYTES_PER_FLOAT32
    + lightSectionByteLength(normalizedLights)
  const buffer = new ArrayBuffer(byteLength)
  const view = new DataView(buffer)
  let offset = 0

  for (let i = 0; i < MAGIC.length; i += 1) {
    view.setUint8(offset, MAGIC.charCodeAt(i))
    offset += 1
  }
  view.setUint32(offset, vertexCount, true)
  offset += BYTES_PER_UINT32
  view.setUint32(offset, indexCount, true)
  offset += BYTES_PER_UINT32
  view.setUint32(offset, primitiveCount, true)
  offset += BYTES_PER_UINT32
  view.setUint32(offset, materialCount, true)
  offset += BYTES_PER_UINT32

  for (const position of positions) {
    view.setFloat32(offset, position, true)
    offset += BYTES_PER_FLOAT32
  }

  for (const normal of normals) {
    view.setFloat32(offset, normal, true)
    offset += BYTES_PER_FLOAT32
  }

  for (const index of indices) {
    view.setUint32(offset, index, true)
    offset += BYTES_PER_UINT32
  }

  for (const primitive of normalizedPrimitives) {
    view.setUint32(offset, primitive.firstIndex, true)
    offset += BYTES_PER_UINT32
    view.setUint32(offset, primitive.indexCount, true)
    offset += BYTES_PER_UINT32
    view.setUint32(offset, primitive.materialIndex, true)
    offset += BYTES_PER_UINT32
  }

  for (const material of normalizedMaterials) {
    for (const color of material.baseColor) {
      view.setFloat32(offset, color, true)
      offset += BYTES_PER_FLOAT32
    }
  }

  // Omitted entirely when there are no lights, so existing models keep their bytes.
  if (normalizedLights.length) {
    offset = writeLightSection(view, offset, normalizedLights, lightSectionVersion(normalizedLights))
  }

  return buffer
}

/** Writes 'LGT0'/'LGT1' plus every record, and returns the offset just past them. */
function writeLightSection(view, start, lights, version) {
  let offset = start
  const magic = version === 1 ? LIGHT_SECTION_MAGIC_V1 : LIGHT_SECTION_MAGIC_V0
  for (let i = 0; i < magic.length; i += 1) {
    view.setUint8(offset, magic.charCodeAt(i))
    offset += 1
  }
  view.setUint32(offset, lights.length, true)
  offset += BYTES_PER_UINT32

  for (const light of lights) {
    if (version === 1) {
      view.setUint32(offset, lightRecordByteLength(light, version), true)
      offset += BYTES_PER_UINT32
    }
    view.setUint32(offset, light.type, true)
    offset += BYTES_PER_UINT32
    for (const value of light.position) {
      view.setFloat32(offset, value, true)
      offset += BYTES_PER_FLOAT32
    }
    if (version === 1) {
      for (const value of light.direction) {
        view.setFloat32(offset, value, true)
        offset += BYTES_PER_FLOAT32
      }
    }
    for (const value of light.color) {
      view.setFloat32(offset, value, true)
      offset += BYTES_PER_FLOAT32
    }
    view.setFloat32(offset, light.intensity, true)
    offset += BYTES_PER_FLOAT32
    if (version === 1) {
      view.setFloat32(offset, light.innerConeAngle, true)
      offset += BYTES_PER_FLOAT32
      view.setFloat32(offset, light.outerConeAngle, true)
      offset += BYTES_PER_FLOAT32
    }
    view.setUint32(offset, light.idBytes.length, true)
    offset += BYTES_PER_UINT32
    for (const byte of light.idBytes) {
      view.setUint8(offset, byte)
      offset += 1
    }
    offset += padding(light.idBytes.length)
  }
  return offset
}

export function readH3dPrototype(buffer) {
  const arrayBuffer = normalizeArrayBuffer(buffer)
  const view = new DataView(arrayBuffer)
  let offset = 0

  requireBytes(view, offset, HEADER_SIZE)
  const magic = String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  )
  offset += 4
  if (magic !== MAGIC) {
    throw new Error(`Invalid H3D prototype magic: ${magic}`)
  }

  const vertexCount = view.getUint32(offset, true)
  offset += BYTES_PER_UINT32
  const indexCount = view.getUint32(offset, true)
  offset += BYTES_PER_UINT32
  const primitiveCount = view.getUint32(offset, true)
  offset += BYTES_PER_UINT32
  const materialCount = view.getUint32(offset, true)
  offset += BYTES_PER_UINT32
  const expectedByteLength = HEADER_SIZE
    + vertexCount * FLOATS_PER_VERTEX * BYTES_PER_FLOAT32
    + vertexCount * FLOATS_PER_VERTEX * BYTES_PER_FLOAT32
    + indexCount * BYTES_PER_UINT32
    + primitiveCount * UINT32S_PER_PRIMITIVE * BYTES_PER_UINT32
    + materialCount * FLOATS_PER_COLOR * BYTES_PER_FLOAT32
  requireBytes(view, 0, expectedByteLength)

  const positions = new Float32Array(vertexCount * FLOATS_PER_VERTEX)
  for (let i = 0; i < positions.length; i += 1) {
    positions[i] = view.getFloat32(offset, true)
    offset += BYTES_PER_FLOAT32
  }

  const normals = new Float32Array(vertexCount * FLOATS_PER_VERTEX)
  for (let i = 0; i < normals.length; i += 1) {
    normals[i] = view.getFloat32(offset, true)
    offset += BYTES_PER_FLOAT32
  }

  const indices = new Uint32Array(indexCount)
  for (let i = 0; i < indices.length; i += 1) {
    indices[i] = view.getUint32(offset, true)
    offset += BYTES_PER_UINT32
  }

  const primitives = []
  for (let i = 0; i < primitiveCount; i += 1) {
    primitives.push({
      firstIndex: view.getUint32(offset, true),
      indexCount: view.getUint32(offset + BYTES_PER_UINT32, true),
      materialIndex: view.getUint32(offset + BYTES_PER_UINT32 * 2, true),
    })
    offset += UINT32S_PER_PRIMITIVE * BYTES_PER_UINT32
  }

  const materials = []
  for (let i = 0; i < materialCount; i += 1) {
    const baseColor = new Float32Array(FLOATS_PER_COLOR)
    for (let colorIndex = 0; colorIndex < baseColor.length; colorIndex += 1) {
      baseColor[colorIndex] = view.getFloat32(offset, true)
      offset += BYTES_PER_FLOAT32
    }
    materials.push({ baseColor })
  }

  const lights = readLightSection(view, offset)

  return {
    positions, normals, indices, primitives, materials, lights,
    baseColor: materials[0]?.baseColor || null,
  }
}

/** Reads the optional trailing light section; a model without one simply has no lights. */
function readLightSection(view, start) {
  let offset = start
  if (offset + LIGHT_SECTION_HEADER_SIZE > view.byteLength) return []

  const magic = String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  )
  const version = magic === LIGHT_SECTION_MAGIC_V0 ? 0 : magic === LIGHT_SECTION_MAGIC_V1 ? 1 : -1
  if (version < 0) return []
  offset += 4

  const lightCount = view.getUint32(offset, true)
  offset += BYTES_PER_UINT32

  const lights = []
  for (let i = 0; i < lightCount; i += 1) {
    const record = version === 1 ? readLightV1(view, offset) : readLightV0(view, offset)
    lights.push(record.light)
    offset = record.nextOffset
  }
  return lights
}

function readLightV0(view, start) {
  let offset = start
  requireBytes(view, offset, LIGHT_V0_FIXED_SIZE)
  const type = view.getUint32(offset, true)
  offset += BYTES_PER_UINT32
  const position = readVector(view, offset)
  offset += FLOATS_PER_LIGHT_VECTOR * BYTES_PER_FLOAT32
  const color = readVector(view, offset)
  offset += FLOATS_PER_LIGHT_VECTOR * BYTES_PER_FLOAT32
  const intensity = view.getFloat32(offset, true)
  offset += BYTES_PER_FLOAT32
  const idBytes = readIdBytes(view, offset)
  offset += BYTES_PER_UINT32 + idBytes.length + padding(idBytes.length)

  return { light: decodeLight({ type, position, color, intensity, idBytes }), nextOffset: offset }
}

function readLightV1(view, start) {
  requireBytes(view, start, LIGHT_V1_FIXED_SIZE)
  const recordByteLength = view.getUint32(start, true)
  if (recordByteLength < LIGHT_V1_FIXED_SIZE || recordByteLength % 4 !== 0) {
    throw new Error(`Invalid H3D light record length: ${recordByteLength}`)
  }
  requireBytes(view, start, recordByteLength)

  let offset = start + BYTES_PER_UINT32
  const type = view.getUint32(offset, true)
  offset += BYTES_PER_UINT32
  const position = readVector(view, offset)
  offset += FLOATS_PER_LIGHT_VECTOR * BYTES_PER_FLOAT32
  const direction = readVector(view, offset)
  offset += FLOATS_PER_LIGHT_VECTOR * BYTES_PER_FLOAT32
  const color = readVector(view, offset)
  offset += FLOATS_PER_LIGHT_VECTOR * BYTES_PER_FLOAT32
  const intensity = view.getFloat32(offset, true)
  offset += BYTES_PER_FLOAT32
  const innerConeAngle = view.getFloat32(offset, true)
  offset += BYTES_PER_FLOAT32
  const outerConeAngle = view.getFloat32(offset, true)
  offset += BYTES_PER_FLOAT32

  const idByteLength = view.getUint32(offset, true)
  if (idByteLength > recordByteLength - LIGHT_V1_FIXED_SIZE) {
    throw new Error(`H3D light id overruns its record: ${idByteLength}`)
  }
  const idBytes = readIdBytes(view, offset)

  // Walk by the record length, not by the fields read: that is what lets a newer
  // writer append fields this reader does not know about.
  return {
    light: decodeLight({ type, position, direction, color, intensity, innerConeAngle, outerConeAngle, idBytes }),
    nextOffset: start + recordByteLength,
  }
}

function decodeLight({ type, position, direction, color, intensity, innerConeAngle, outerConeAngle, idBytes }) {
  const typeName = LIGHT_TYPE_NAMES[type]
  if (!typeName) throw new Error(`Unsupported H3D light type id: ${type}`)

  const light = { id: new TextDecoder().decode(idBytes), type: typeName, position, color, intensity }
  if (typeName === 'spot') {
    light.direction = direction ?? new Float32Array(FLOATS_PER_LIGHT_VECTOR)
    light.innerConeAngle = innerConeAngle ?? DEFAULT_INNER_CONE_ANGLE
    light.outerConeAngle = outerConeAngle ?? DEFAULT_OUTER_CONE_ANGLE
  }
  return light
}

function readVector(view, offset) {
  const values = new Float32Array(FLOATS_PER_LIGHT_VECTOR)
  for (let axis = 0; axis < values.length; axis += 1) {
    values[axis] = view.getFloat32(offset + axis * BYTES_PER_FLOAT32, true)
  }
  return values
}

/** Reads a uint32 length immediately followed by that many UTF-8 bytes. */
function readIdBytes(view, offset) {
  const idByteLength = view.getUint32(offset, true)
  requireBytes(view, offset + BYTES_PER_UINT32, idByteLength)
  const idBytes = new Uint8Array(idByteLength)
  for (let byte = 0; byte < idByteLength; byte += 1) {
    idBytes[byte] = view.getUint8(offset + BYTES_PER_UINT32 + byte)
  }
  return idBytes
}

function validatePositions(positions) {
  if (!positions || typeof positions.length !== 'number' || positions.length % FLOATS_PER_VERTEX !== 0) {
    throw new Error('positions must contain x,y,z float triples')
  }
}

function validateNormals(normals, positionLength) {
  if (!normals || typeof normals.length !== 'number' || normals.length % FLOATS_PER_VERTEX !== 0) {
    throw new Error('normals must contain x,y,z float triples')
  }
  if (normals.length !== positionLength) {
    throw new Error('normals length must match positions length')
  }
  for (const normal of normals) {
    if (!Number.isFinite(normal)) {
      throw new Error('normal values must be finite')
    }
  }
}

function validateIndices(indices, vertexCount) {
  if (!indices || typeof indices.length !== 'number') {
    throw new Error('indices must be an array-like list of uint32 values')
  }
  for (const index of indices) {
    if (!Number.isInteger(index) || index < 0 || index >= vertexCount) {
      throw new Error('indices must be uint32 values inside the vertex buffer')
    }
  }
}

function normalizePrimitives(primitives, indexCount) {
  if (primitives) return primitives
  return [{ firstIndex: 0, indexCount, materialIndex: 0 }]
}

function normalizeMaterials(materials, baseColor) {
  if (materials) return materials
  return [{ baseColor }]
}

function validatePrimitives(primitives, indexCount, materialCount) {
  if (!Array.isArray(primitives) || primitives.length === 0) {
    throw new Error('primitives must contain at least one renderable primitive')
  }
  for (const primitive of primitives) {
    if (!Number.isInteger(primitive.firstIndex) || !Number.isInteger(primitive.indexCount) || !Number.isInteger(primitive.materialIndex)) {
      throw new Error('primitive ranges and material indices must be uint32 values')
    }
    if (primitive.firstIndex < 0 || primitive.indexCount <= 0 || primitive.firstIndex + primitive.indexCount > indexCount) {
      throw new Error('primitive index range is outside the global index buffer')
    }
    if (primitive.materialIndex < 0 || primitive.materialIndex >= materialCount) {
      throw new Error('primitive materialIndex is outside the material table')
    }
  }
}

function validateMaterials(materials) {
  if (!Array.isArray(materials) || materials.length === 0) {
    throw new Error('materials must contain at least one base-color material')
  }
  for (const material of materials) validateBaseColor(material.baseColor)
}

function validateBaseColor(baseColor) {
  if (!baseColor || typeof baseColor.length !== 'number' || baseColor.length !== FLOATS_PER_COLOR) {
    throw new Error('baseColor must contain r,g,b,a float values')
  }
  for (const color of baseColor) {
    if (!Number.isFinite(color)) {
      throw new Error('baseColor values must be finite')
    }
  }
}

/** Bytes of zero padding needed to keep the next record 4-byte aligned. */
function padding(byteLength) {
  return (4 - (byteLength % 4)) % 4
}

/** LGT1 only appears once a model actually contains a light LGT0 cannot describe. */
function lightSectionVersion(lights) {
  return lights.some(light => light.type === LIGHT_TYPE_SPOT) ? 1 : 0
}

function lightRecordByteLength(light, version) {
  const fixed = version === 1 ? LIGHT_V1_FIXED_SIZE : LIGHT_V0_FIXED_SIZE
  return fixed + light.idBytes.length + padding(light.idBytes.length)
}

function lightSectionByteLength(lights) {
  if (!lights.length) return 0
  const version = lightSectionVersion(lights)
  return lights.reduce(
    (total, light) => total + lightRecordByteLength(light, version),
    LIGHT_SECTION_HEADER_SIZE,
  )
}

function normalizeLights(lights) {
  if (lights === undefined || lights === null) return []
  if (!Array.isArray(lights)) throw new Error('lights must be an array of punctual lights')

  const seen = new Set()
  return lights.map(light => {
    const type = LIGHT_TYPE_IDS[light?.type]
    if (type === undefined) throw new Error(`Unsupported H3D light type: ${light?.type}`)

    const id = String(light.id ?? '')
    if (!id) throw new Error('each light needs a stable id')
    if (seen.has(id)) throw new Error(`duplicate light id: ${id}`)
    seen.add(id)

    const intensity = Number(light.intensity)
    if (!Number.isFinite(intensity)) throw new Error('light intensity must be finite')

    return {
      id,
      type,
      position: validateLightVector(light.position, 'light position'),
      color: validateLightVector(light.color, 'light color'),
      intensity,
      idBytes: new TextEncoder().encode(id),
      // Written for every record of an LGT1 section, so a Point in a mixed model still
      // has well-defined bytes there: a zero axis and zero angles the reader ignores.
      ...(type === LIGHT_TYPE_SPOT ? normalizeSpot(light) : { direction: [0, 0, 0], innerConeAngle: 0, outerConeAngle: 0 }),
    }
  })
}

/** Normalizes the cone axis and pulls both angles into the range glTF allows. */
function normalizeSpot(light) {
  const direction = validateLightVector(light.direction, 'light direction')
  const length = Math.hypot(direction[0], direction[1], direction[2])
  if (!(length > 0) || !Number.isFinite(length)) throw new Error('light direction must not be a zero vector')

  const rawOuter = Number(light.outerConeAngle)
  const outerConeAngle = Math.min(
    Number.isFinite(rawOuter) && rawOuter > 0 ? rawOuter : DEFAULT_OUTER_CONE_ANGLE,
    MAX_CONE_ANGLE,
  )
  const rawInner = Number(light.innerConeAngle)
  const innerConeAngle = Number.isFinite(rawInner)
    ? Math.min(Math.max(rawInner, 0), outerConeAngle)
    : DEFAULT_INNER_CONE_ANGLE

  return {
    direction: [direction[0] / length, direction[1] / length, direction[2] / length],
    innerConeAngle,
    outerConeAngle,
  }
}

function validateLightVector(values, label) {
  if (!values || typeof values.length !== 'number' || values.length !== FLOATS_PER_LIGHT_VECTOR) {
    throw new Error(`${label} must contain three float values`)
  }
  for (const value of values) {
    if (!Number.isFinite(value)) throw new Error(`${label} values must be finite`)
  }
  return [values[0], values[1], values[2]]
}

function normalizeArrayBuffer(buffer) {
  if (buffer instanceof ArrayBuffer) return buffer
  if (ArrayBuffer.isView(buffer)) {
    return buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength)
  }
  throw new Error('buffer must be an ArrayBuffer or ArrayBuffer view')
}

function requireBytes(view, offset, byteLength) {
  if (offset + byteLength > view.byteLength) {
    throw new Error('Truncated H3D prototype buffer')
  }
}
